import React from "react"
import { v4 as uuid } from "uuid"
import { useMemberActionTracking } from "#/services/memberActionTracking/useMemberActionTracking"
import { MemberActions } from "#/services/memberActionTracking/memberActions"
import type { IntakeTracker } from "./intakeTrackingTypes"

const IntakeTrackingContext = React.createContext<IntakeTracker | null>(null)

export function IntakeTrackingProvider({
  appointmentId,
  children
}: React.PropsWithChildren<{ appointmentId: string }>) {
  const { mutate } = useMemberActionTracking()
  const recordRef = React.useRef(mutate)
  recordRef.current = mutate
  // The route keys this provider by appointment. Unmounting ends the intake session;
  // callbacks retained by pending submissions can still finish its original sequence.
  const [track] = React.useState<IntakeTracker>((): IntakeTracker => {
    const intakeFlowId = uuid()
    let eventSequence = 0
    let intakeViewed = false
    return (memberActionTaken, metadata = {}) => {
      if (memberActionTaken === MemberActions.PREPARE_FOR_VISIT_VIEWED) {
        if (intakeViewed) return
        intakeViewed = true
      }
      try {
        recordRef.current({
          memberActionTaken,
          metaData: { ...metadata, appointmentId, intakeFlowId, eventSequence: ++eventSequence }
        })
      } catch {
        // Tracking must not interrupt intake or change the outcome of a submission.
      }
    }
  })
  return <IntakeTrackingContext.Provider value={track}>{children}</IntakeTrackingContext.Provider>
}

export function useIntakeTracker() {
  const track = React.useContext(IntakeTrackingContext)
  if (!track) throw new Error("Intake tracking requires IntakeTrackingProvider")
  return track
}
